/**
 * @file   carwash_website\Carwash-FrontEnd\src\Pages\CampaignReport.tsx
 * @brief  Campaign Codes report page for each carwash.
 * @date   MAY, 2025
 * @author ZCO Engineer
 * @copyright (c) 2025, ZCO
 */

import {
  Container,
  Row,
  Col,
  Breadcrumb,
  useNavigate,
  useEffect,
  useOutletContext,
  useState,
  useParams,
  Pagination,
  React,
  Button,
} from "../utils/thirdpartyComponents";
import { userType } from "../utils/enums";
import { pageURLs } from "../utils/pageUrls";
import { AppContextType, CampaignRecord } from "../interfaces/generalInterface";
import axios from "../config/axiosConfig";
import { CompanyApi, CampaignApi } from "../utils/apiUrls";
import SmallLoader from "../components/SmallLoader";
import LocationIcon from "../assets/images/icons/locationicon";
import CWMultiSelect from "../components/CWMultiSelect";
import { MessageToaster } from "../utils/toastUtil";
import { CampaignItem, Option } from "../interfaces/userInterface";
import moment from "moment-timezone";

const CampaignReport = (props: any) => {
  // Access the context
  const { updateData } = useOutletContext<AppContextType>();
  const carwash_owner =
    props.userType === userType["CARWASH-OWNER"] ? true : false;
  const toaster = new MessageToaster();
  const navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [campaignRecords, setCampaignRecords] = useState<CampaignRecord[]>([]);

  // State to handle campaigns given in dropdown
  const [campaigns, setCampaigns] = useState<Option[]>([]);

  // State to handle selected campaign form dropdown
  const [selectedCampaign, setSelectedCampaign] = useState<Option[]>([]);

  useEffect(() => {
    if (campaigns.length > 0 && selectedCampaign.length === 0) {
      setSelectedCampaign(campaigns);
    }
  }, [campaigns]);

  const handleSelectChange = (selectedOption: any) => {
    setSelectedCampaign(selectedOption);
  };

  const getCompanyCampaigns = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        CampaignApi.CARWASH_OWNER_GET_COMPANY_CAMPAIGNS
      );
      if (data.error === 0) {
        const campaign_list: CampaignItem[] = data?.data;

        // Transform the array
        const transformedArray: Option[] = campaign_list.map((item) => ({
          label: item.campaignName,
          value: item.campaignID,
        }));

        setCampaigns(transformedArray);
      } else {
        // show error message
        toaster.toastError(data.message);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCompanyCampaigns();
  }, []);

  // State to company details
  const [company, setCompany] = useState({
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  // To display heading
  useEffect(() => {
    getCompanyAddress();
  }, []);

  useEffect(() => {
    handleUpdate();
  }, [company]);

  const getCompanyAddress = async () => {
    try {
      const id = params.id ? parseInt(params.id) : 0;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
        : await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id);
      if (data?.error === 0) {
        setCompany(data.data);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  // Update the data
  const handleUpdate = () => {
    updateData({
      heading: "Campaign Codes Report",
      url: "",
      showBackButton: false,
      showMainMenu: true,
      activeLink: "reports",
    });
  };

  // Navigate accordung to url
  const itemClick = async (url: string) => {
    navigate(url);
  };

  const getRecords = async (pageNo = 1) => {
    try {
      setLoading(true);
      let param;
      param = `?pageNo=${pageNo}&pageSize=12`;

      // Need to be considered only if filter by is done.
      if (selectedCampaign.length > 0) {
        const campaignIds = selectedCampaign
          .map((campaign) => campaign.value)
          .join(",");
        param += "&campaignId=" + campaignIds;
      }

      const { data } = await axios.get(CampaignApi.GET_CAMPAIGN_REPORT + param);
      if (data?.error === 0 && Array.isArray(data?.data?.list)) {
        const report_list = data.data.list;
        if (report_list.length > 0) {
          setCampaignRecords(report_list);
          setTotalPages(Math.ceil(data.data.totalCount / data.data.limit) || 1);
        } else {
          console.log("Report list is empty");
          setCampaignRecords([]);
        }
      } else {
        console.log("Report list is not available or is not an array");
        setCampaignRecords([]);
      }
      setLoading(false);
    } catch (error: any) {
      console.error("Error fetching report list:", error.message);
      setLoading(false);
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  useEffect(() => {
    getRecords(currentPage);
  }, [selectedCampaign, currentPage]);

  // To format date
  const formatDate = (createdDate: string) => {
    const date = moment(createdDate);
    return date.format("MMM D YYYY");
  };

  const getVisiblePageNumbers = () => {
    const totalPageNumbersToShow = 5;
    let startPage = Math.max(
      1,
      currentPage - Math.floor(totalPageNumbersToShow / 2)
    );
    let endPage = startPage + totalPageNumbersToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  //genererated codes download function
  const handleDownload = (excelFileName: string) => {
    const fileUrl = `${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${excelFileName}`;
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = excelFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRedeemedReport = async (campaignID: number) => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        CampaignApi.GET_REDEEMED_API + campaignID
      );
      if (data?.error === 0) {
        const campaign_report: string = data?.data;
        handleDownload(campaign_report);
        setLoading(false);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  return (
    <div className="manage-carwash-container order-list-container">
      <Container>
        <Breadcrumb>
          <Breadcrumb.Item onClick={() => itemClick(`/${pageURLs.dashboard}`)}>
            Dashboard
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Campaign Codes Report</Breadcrumb.Item>
        </Breadcrumb>

        <div className="manage-header-block d-flex justify-content-between align-items-center">
          <div className="search-wrap w-100">
            <div className="result-group d-flex align-items-center">
              <p className="title f-bold">Results for</p>
              <CWMultiSelect
                id="campaign"
                name="campaign"
                label="Select Campaign"
                options={campaigns}
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select Campaign"
                onChange={(selectedOptions) =>
                  handleSelectChange(selectedOptions)
                }
                hasSelectAll={true}
                value={selectedCampaign} // Set all selected by default
                labelAllSelected="All Campaigns Selected."
              />
            </div>
          </div>
        </div>
        <div className="cw-con-report-wrap">
          <Row>
            {campaignRecords.length > 0 ? (
              campaignRecords.map((record, index) => (
                <Col xs={12} md={6} xl={4} key={index}>
                  <div className="cw-con-report-card">
                    <div className="cw-con-report-cardtop">
                      <div>
                        <h2>Campaign Name</h2>
                        <h3>{record.CampaignName}</h3>
                      </div>
                      <div className="campaign-place">
                        <LocationIcon />
                        <span className="location-padding">
                          {record.LocationName}
                        </span>
                      </div>
                    </div>
                    <div className="cw-con-report-cardmiddle">
                      <div className="d-flex w-50 me-1 me-sm-3">
                        <div className="cw-con-report-cardmiddle-left">
                          <h2>
                            No. of <br />
                            Codes generated
                          </h2>
                          {record.NoOfCodes && record.NoOfCodes > 0 ? (
                            <Button
                              variant="light"
                              className="light-btn action-btn px-0"
                              onClick={() =>
                                handleDownload(record.ReportURL ?? "")
                              }
                            >
                              Download
                            </Button>
                          ) : (
                            ""
                          )}
                        </div>
                        <div className="cw-con-report-cardmiddle-right cw-con-bg-yellow">
                          {record.NoOfCodes === null ? 0 : record.NoOfCodes}
                        </div>
                      </div>
                      <div className="d-flex w-50 ms-1 ms-sm-3">
                        <div className="cw-con-report-cardmiddle-left">
                          <h2>
                            No. of <br />
                            Codes redeemed
                          </h2>
                          {record.NoOfCodesRedeemed &&
                          record.NoOfCodesRedeemed > 0 ? (
                            <Button
                              variant="light"
                              className="light-btn action-btn px-0"
                              onClick={() =>
                                handleRedeemedReport(record.CampaignID)
                              }
                            >
                              Download
                            </Button>
                          ) : (
                            ""
                          )}
                        </div>
                        <div className="cw-con-report-cardmiddle-right cw-con-bg-blue">
                          {record.NoOfCodesRedeemed === null
                            ? 0
                            : record.NoOfCodesRedeemed}
                        </div>
                      </div>
                    </div>
                    <div className="cw-con-report-cardbtm">
                      <div className="w-50">
                        <h2>Discount Value</h2>
                        <h4>
                          {record.DiscountType === 1
                            ? `$${record.DiscountValue}`
                            : `${record.DiscountValue}%`}
                        </h4>
                      </div>
                      <div className="w-50">
                        <h2>Campaign Period</h2>
                        <h4>
                          {formatDate(record.ValidFrom)} -{" "}
                          {formatDate(record.ValidTo)}
                        </h4>
                      </div>
                    </div>
                  </div>
                </Col>
              ))
            ) : (
              <tr>
                <Col xs={12} md={12} xl={12} style={{ textAlign: "center" }}>
                  No data found.
                </Col>
              </tr>
            )}
          </Row>
          {campaignRecords.length ? (
            <div className="mt-3 d-flex justify-content-end">
              <Pagination>
                <Pagination.First
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(1)}
                />
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                />
                {getVisiblePageNumbers().map((pageNumber) => (
                  <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === currentPage}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(totalPages)}
                />
              </Pagination>
            </div>
          ) : null}
        </div>
      </Container>

      {loading && <SmallLoader />}
    </div>
  );
};

export default CampaignReport;
